import { useMemo } from 'react';
import { calculateSetupScore } from '../utils/technicalCalculator';

export const useTradeStats = (rows, initialBalance = 10000, userProfile = null, dateFilter = "All") => {
    const stats = useMemo(() => {
        let balance = initialBalance;
        let totalWins = 0;
        let totalLosses = 0;
        let totalWinPnl = 0;
        let totalLossPnl = 0;
        let totalTrades = 0;
        let totalLots = 0;
        let grossProfit = 0;
        let grossLoss = 0;

        let totalR = 0;
        let totalWinR = 0;
        let totalLossR = 0;
        let countWinR = 0;
        let countLossR = 0;
        let countTradesWithR = 0;

        const equityCurve = [{ date: 'Start', balance: initialBalance }];

        // Sort rows by date ascending for the chart
        const sortedRows = [...rows].sort((a, b) => new Date(a.date) - new Date(b.date));

        let peak = initialBalance;
        let maxDrawdown = 0;
        let maxDrawdownAmount = 0;

        const plannedCandidates = [];

        sortedRows.forEach(trade => {
            // Filter: Only process "Done" trades for statistics
            if (trade.status !== 'Done') {
                return;
            }

            totalTrades++;

            // Parse PnL
            const pnlString = trade.pnl ? String(trade.pnl).replace(/[^\d.-]/g, '') : '0';
            const pnl = parseFloat(pnlString);

            if (!isNaN(pnl)) {
                balance += pnl;
                equityCurve.push({
                    date: trade.date,
                    balance: balance,
                    pnl: pnl
                });

                // Max Drawdown Calculation
                if (balance > peak) {
                    peak = balance;
                }
                const currentDrawdownAmount = peak - balance;
                if (currentDrawdownAmount > maxDrawdownAmount) {
                    maxDrawdownAmount = currentDrawdownAmount;
                }
                const currentDrawdown = peak > 0 ? (currentDrawdownAmount / peak) * 100 : 0;
                if (currentDrawdown > maxDrawdown) {
                    maxDrawdown = currentDrawdown;
                }

                if (pnl > 0) {
                    totalWins++;
                    totalWinPnl += pnl;
                    grossProfit += pnl;
                } else if (pnl < 0) {
                    totalLosses++;
                    totalLossPnl += Math.abs(pnl);
                    grossLoss += Math.abs(pnl);
                }

                // Realized R calculation
                const parseNum = (v) => {
                    if (v === null || v === undefined) return null;
                    if (typeof v === 'number') return v;
                    const cleaned = String(v).replace(/[^0-9.-]/g, '');
                    const n = parseFloat(cleaned);
                    return isNaN(n) ? null : n;
                };

                const entryP = parseNum(trade.entry);
                const slP = parseNum(trade.sl);
                const exitP = parseNum(trade.exit);
                const isLong = !String(trade.side || 'BUY').toUpperCase().includes('SHORT') && !String(trade.side || 'BUY').toUpperCase().includes('SELL');

                let rRealized = null;
                if (entryP && slP && exitP !== null && entryP !== slP) {
                    const riskDist = Math.abs(entryP - slP);
                    rRealized = isLong ? (exitP - entryP) / riskDist : (entryP - exitP) / riskDist;
                } else if (trade.rr_realized !== undefined && trade.rr_realized !== null) {
                    rRealized = parseNum(trade.rr_realized);
                } else if (trade.r_multiple !== undefined && trade.r_multiple !== null) {
                    rRealized = parseNum(trade.r_multiple);
                }

                if (rRealized !== null && !isNaN(rRealized)) {
                    countTradesWithR++;
                    totalR += rRealized;
                    if (rRealized > 0) {
                        countWinR++;
                        totalWinR += rRealized;
                    } else if (rRealized < 0) {
                        countLossR++;
                        totalLossR += Math.abs(rRealized);
                    }
                }

                // Collect qualified planned trade data for empirical calibration
                const tpP = parseNum(trade.tp);
                let plannedRR = null;
                if (entryP && slP && tpP !== null && entryP !== slP) {
                    const riskDist = Math.abs(entryP - slP);
                    const rewardDist = Math.abs(tpP - entryP);
                    if (riskDist > 0) plannedRR = rewardDist / riskDist;
                } else if (trade.rr_planned !== undefined && trade.rr_planned !== null) {
                    plannedRR = parseNum(trade.rr_planned);
                }

                if (plannedRR !== null && plannedRR > 0) {
                    let sSetup = 0.65;
                    // Pure ex-ante Setup Score: immune to post-entry exit discipline contamination
                    if (trade.view) {
                        try {
                            const setupRes = calculateSetupScore(trade.view, plannedRR);
                            if (setupRes && setupRes.score !== undefined) {
                                sSetup = setupRes.score / 100.0;
                            }
                        } catch (e) {
                            // ignore parse error and fallback
                        }
                    } else if (trade.setup_score !== undefined && trade.setup_score !== null) {
                        const num = Number(trade.setup_score);
                        sSetup = num > 1.0 ? num / 100.0 : num;
                    } else if (trade.setupScore !== undefined && trade.setupScore !== null) {
                        const num = Number(trade.setupScore);
                        sSetup = num > 1.0 ? num / 100.0 : num;
                    }
                    // Record candidate without mutating original trade object
                    plannedCandidates.push({ plannedRR, sSetup });
                }
            }

            // Parse Lots
            const lotString = trade.size ? String(trade.size).replace(/[^\d.]/g, '') : '0';
            const lots = parseFloat(lotString);
            if (!isNaN(lots)) {
                totalLots += lots;
            }
        });

        const netProfit = balance - initialBalance;
        const roi = initialBalance > 0 ? (netProfit / initialBalance) * 100 : 0;
        const recoveryFactor = maxDrawdownAmount > 0
            ? (netProfit / maxDrawdownAmount)
            : (netProfit > 0 ? Infinity : 0);

        const winRate = totalTrades > 0 ? (totalWins / totalTrades) * 100 : 0;
        const avgWin = totalWins > 0 ? totalWinPnl / totalWins : 0;
        const avgLoss = totalLosses > 0 ? totalLossPnl / totalLosses : 0;
        const profitFactor = grossLoss > 0 ? grossProfit / grossLoss : (grossProfit > 0 ? Infinity : 0);
        const avgRRR = avgLoss > 0 ? avgWin / avgLoss : 0;
        const expectancy = (winRate / 100 * avgWin) - ((1 - winRate / 100) * avgLoss);

        // Expectancy in R (Universal Edge of the System)
        const avgWinR = countWinR > 0 ? (totalWinR / countWinR) : null;
        const avgLossR = countLossR > 0 ? (totalLossR / countLossR) : null;
        const expectancyR = countTradesWithR > 0
            ? (totalR / countTradesWithR)
            : (avgLoss > 0 ? (expectancy / avgLoss) : 0);

        // Historically Calibrated Global Planned Expectancy via Bayesian Shrinkage:
        // Anchors baseline win probability using an empirical Beta(10, 10) prior (k=20, prior=0.50).
        // Resolves small sample variance for young accounts while asymptotically converging to empirical win rate:
        const SHRINKAGE_K = 20.0;
        const historicalWinRateDecimal = (totalWins + SHRINKAGE_K * 0.50) / (totalTrades + SHRINKAGE_K);
        let sumCalibratedERPlanned = 0;

        plannedCandidates.forEach(({ plannedRR, sSetup }) => {
            // Calibrate: Setup Score 0.65 is baseline; delta modulates p_gain around actual win rate
            const pGain = Math.min(0.85, Math.max(0.15, historicalWinRateDecimal + (sSetup - 0.65) * 0.50));
            const pLoss = 1.0 - pGain;
            const erPlanned = (pGain * plannedRR) - (pLoss * 1.0);
            sumCalibratedERPlanned += erPlanned;
        });

        const validPlannedCount = plannedCandidates.length;

        const expectancyRPlanned = validPlannedCount > 0
            ? Number((sumCalibratedERPlanned / validPlannedCount).toFixed(2))
            : null;

        const leakageR = expectancyRPlanned !== null && expectancyRPlanned !== undefined
            ? Number((expectancyR - expectancyRPlanned).toFixed(2))
            : null;

        // Sharpe Ratio (Simplified: Mean Return / StdDev of Returns)
        const returns = equityCurve.slice(1).map(point => point.pnl);
        const meanReturn = returns.reduce((a, b) => a + b, 0) / returns.length || 0;
        const variance = returns.reduce((a, b) => a + Math.pow(b - meanReturn, 2), 0) / returns.length || 0;
        const stdDev = Math.sqrt(variance);
        const sharpeRatio = stdDev > 0 ? meanReturn / stdDev : 0;

        let result = {
            balance,
            equity: balance, // Assuming all trades closed
            netProfit,
            roi,
            recoveryFactor,
            winRate,
            avgWin,
            avgLoss,
            totalTrades,
            totalLots,
            profitFactor,
            avgRRR,
            expectancy,
            totalR: Number(totalR.toFixed(2)),
            expectancyR,
            expectancyRPlanned,
            leakageR,
            avgWinR,
            avgLossR,
            sharpeRatio,
            maxDrawdown,
            maxDrawdownAmount,
            equityCurve
        };

        // Override with explicit backend source of truth if viewing "All Time"
        if (dateFilter === "All" && userProfile) {
            const finalBalance = userProfile.balance ?? result.balance;
            const finalNetProfit = userProfile.net_profit ?? (finalBalance - initialBalance);
            const finalMaxDrawdown = userProfile.max_drawdown ?? result.maxDrawdown;
            const finalRoi = initialBalance > 0 ? (finalNetProfit / initialBalance) * 100 : result.roi;
            const finalRecoveryFactor = finalMaxDrawdown > 0 && finalRoi !== undefined
                ? (finalRoi / finalMaxDrawdown)
                : result.recoveryFactor;

            result = {
                ...result,
                balance: finalBalance,
                equity: userProfile.equity ?? result.equity,
                netProfit: finalNetProfit,
                roi: finalRoi,
                recoveryFactor: finalRecoveryFactor,
                winRate: userProfile.win_rate ?? result.winRate,
                maxDrawdown: finalMaxDrawdown,
                expectancy: userProfile.expectancy ?? result.expectancy,
                profitFactor: userProfile.profit_factor ?? result.profitFactor,
                totalTrades: userProfile.total_trades ?? result.totalTrades
            };
        }

        return result;
    }, [rows, initialBalance, userProfile, dateFilter]);

    return stats;
};
